import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import "@/App.css";
import "@/lib/axiosInterceptors"; // Registers axios interceptors globally (admin JWT Bearer)
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import VendorDashboard from "./pages/VendorDashboard";
import CourierDashboard from "./pages/CourierDashboard";
import CourierProfilePage from "./pages/CourierProfilePage";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerProducts from "./pages/PartnerProducts";
import PartnerShowcasesPage from "./pages/PartnerShowcasesPage";
import NewOrder from "./pages/NewOrder";
import ProductsPage from "./pages/ProductsPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import OrderTracking from "./pages/OrderTracking";
import AdminPanel from "./pages/AdminPanel";
import Cockpit from "./pages/admin/Cockpit";
import QRCodesPage from "./pages/QRCodesPage";
import InstallPage from "./pages/InstallPage";
import CreditsPage from "./pages/CreditsPage";
import ArtisanPublicProfile from "./pages/ArtisanPublicProfile";
import { GeoProvider } from "@/components/GeoProvider";
import { AppErrorBoundary, NotFoundPage } from "@/components/AppErrorBoundary";
import HomePage from "./pages/HomePage";
import PublicTracking from "./pages/PublicTracking";
import FoodPage from "./pages/FoodPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import ArtisanDashboard from "./pages/ArtisanDashboard";
import ArtisanProfileEdit from "./pages/ArtisanProfileEdit";
import CargoOrder from "./pages/CargoOrder";
import TaxiOrder from "./pages/TaxiOrder";
import Leaderboard from "./pages/Leaderboard";
import ArtisanList from "./pages/ArtisanList";
import ArtisanProjectNew from "./pages/ArtisanProjectNew";
import ArtisanProjectDetail from "./pages/ArtisanProjectDetail";
import SmartAssistant from "./components/SmartAssistant";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PushNotificationManager from "./components/PushNotificationManager";
import { Toaster } from "./components/ui/sonner";

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    // Sessions sans jeton (avant la sécurisation) : reconnexion unique demandée
    if (savedUser && !localStorage.getItem('auth_token')) {
      localStorage.removeItem('user');
      return null;
    }
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Hide splash screen after 1.5s
    const timer = setTimeout(() => setShowSplash(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = (userData, token) => {
    if (token) localStorage.setItem('auth_token', token);
    // Multi-profils : le profil actif pilote les gardes de route
    userData = { ...userData, role: userData.active_role || userData.role };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
  };

  // PWA Splash Screen
  if (showSplash && window.matchMedia('(display-mode: standalone)').matches) {
    return (
      <div className="pwa-splash">
        <img src="/icons/icon-192x192.png" alt="ARTEKNOS" className="pwa-splash-logo" />
        <h1 className="pwa-splash-title">ARTEKNOS</h1>
        <p className="pwa-splash-subtitle">Livraison Express</p>
        <div className="pwa-splash-loader"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <GeoProvider user={user} />
      <BrowserRouter>
        <RoutedErrorBoundary>
        <Routes>
          <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} />} />
          <Route path="/auth" element={user ? <Navigate to={
            user.role === 'courier' ? '/courier' :
            user.role === 'partner' ? '/partner' :
            user.role === 'vendor' ? '/vendor' :
            user.role === 'artisan' ? '/artisans/dashboard' : '/home'
          } /> : <AuthPage onLogin={handleLogin} />} />
          <Route path="/dashboard" element={user ? <Dashboard user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/vendor" element={user && user.role === 'vendor' ? <VendorDashboard user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/courier" element={user && user.role === 'courier' ? <CourierDashboard user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/courier/profile" element={user && user.role === 'courier' ? <CourierProfilePage user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/partner" element={user && user.role === 'partner' ? <PartnerDashboard user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/partner/products" element={user && user.role === 'partner' ? <PartnerProducts user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/partner/ads" element={user && user.role === 'partner' ? <PartnerShowcasesPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/orders/new" element={user ? <NewOrder user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/products" element={<ProductsPage user={user} onLogout={handleLogout} />} />
          <Route path="/appointments" element={user ? <AppointmentsPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/payment-success" element={user ? <PaymentSuccess user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/tracking" element={user ? <OrderTracking user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/credits" element={user ? <CreditsPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/home" element={user ? <HomePage user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/food" element={user ? <FoodPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/loyalty" element={user ? <LoyaltyPage user={user} /> : <Navigate to="/auth" />} />
          <Route path="/cargo" element={user ? <CargoOrder user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/taxi" element={user ? <TaxiOrder user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/leaderboard" element={<Leaderboard user={user} onLogout={handleLogout} />} />
          <Route path="/artisans" element={<ArtisanList user={user} onLogout={handleLogout} />} />
          <Route path="/artisans/dashboard" element={user && user.role === 'artisan' ? <ArtisanDashboard user={user} onUserUpdate={setUser} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/artisans/profile/edit" element={user && user.role === 'artisan' ? <ArtisanProfileEdit user={user} onUserUpdate={setUser} /> : <Navigate to="/auth" />} />
          <Route path="/artisans/new" element={user ? <ArtisanProjectNew user={user} /> : <Navigate to="/auth" />} />
          <Route path="/artisans/projects/:projectId" element={<ArtisanProjectDetail user={user} />} />
          <Route path="/artisans/profile" element={<Navigate to="/artisans/profile/edit" replace />} />
          <Route path="/artisans/:artisanId" element={<ArtisanPublicProfile user={user} />} />
          <Route path="/admin-private-126" element={<AdminPanel />} />
          <Route path="/admin/cockpit" element={<Cockpit />} />
          <Route path="/qrcodes" element={<QRCodesPage />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/track/public/:token" element={<PublicTracking />} />
          {/* Catch-all : page manquante → message clair + retour (jamais de page blanche) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </RoutedErrorBoundary>
      </BrowserRouter>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Push Notifications Manager */}
      <PushNotificationManager user={user} />

      {/* Assistant IA ARTEKNOS (flottant, visible uniquement si connecté) */}
      <SmartAssistant user={user} />

      <Toaster position="top-right" />
    </div>
  );
}


// Réinitialise l'écran d'erreur à chaque changement de page
function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <AppErrorBoundary resetKey={location.pathname}>{children}</AppErrorBoundary>;
}

export default App;
